/**
 * When an event is over - the one rule for the feed, RSVP/going, group joins, Maps, Sets and Find.
 *
 * An event is over at 6 AM on the morning after its LAST day (sets run past midnight; nothing runs past 6).
 * The server computes it as events.ends_at (migration 134): the last day is the later of event_date and the
 * lineup's last performance_day and a pass's named length ("2-Day Pass", migration 153), and a multi_day event with
 * neither is assumed to run three days.
 * Every event row the app fetches carries ends_at; the fallback below is the same rule for a row that
 * does not (a cache persisted before 134), and it cannot see the lineup, so it is only an approximation.
 *
 * Replaced (2026-09-28): feed start + 0/2 days by venue tier, RSVP start + 7/9 days, Maps +3, Find +4 -
 * which left Portola and CRSSD in the feed and RSVP-able on the Monday after they ended.
 */

import { getEventStartMs } from '../lib/proximity/data/eventStart';

const DAY_MS = 24 * 60 * 60 * 1000;
const NIGHT_ENDS_HOUR = 6;
/** multi_day with no known last day: three days (Sam, 2026-09-28). Mirrors event_ends_at() in 134. */
const MULTI_DAY_ASSUMED_EXTRA_DAYS = 2;

/**
 * How far back the feed asks the server to look. A festival that started this long ago can still be on
 * (a four-day run on its last night); the server drops anything already over (ends_at), so asking wider
 * costs nothing but a few rows.
 */
export const MAX_DISPLAY_GRACE_DAYS = 4;

/** The event's first day as a local calendar date. A bare date is that day, not UTC midnight. */
function firstDay(event) {
  const raw = event?.event_date || event?.event_datetime || null;
  if (!raw) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(raw));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/** When the event is over, as a Date; null when it has no date (never treated as over). */
export function getEventEndsAt(event) {
  if (!event) return null;
  const fromServer = event.ends_at ?? event.endsAt ?? null;
  if (fromServer) {
    const t = new Date(fromServer);
    if (!Number.isNaN(t.getTime())) return t;
  }
  const day = firstDay(event);
  if (!day) return null;
  const extra = event.multi_day || event.multiDay ? MULTI_DAY_ASSUMED_EXTRA_DAYS : 0;
  const ends = new Date(day);
  ends.setDate(ends.getDate() + extra + 1);
  ends.setHours(NIGHT_ENDS_HOUR, 0, 0, 0);
  return ends;
}

export function isEventOver(event, now = new Date()) {
  const ends = getEventEndsAt(event);
  return ends != null && now.getTime() >= ends.getTime();
}

/**
 * Find and the people layer outlive the event (Sam, 2026-10-04): after the last set people find their
 * friends, the exit, the pickup point, the car, the camp. Since migration 147 (Sam, 2026-10-12) that tail is
 * 3 h past ends_at for every event (it was 6 h for a show, 12 h for a festival). The server's window
 * (event_presence_window) and userGroups.ts isFindOver carry the same numbers.
 */
export const FIND_TAIL_HOURS = { show: 3, festival: 3 };
/** Location may run from this long before an event's first start (migration 147: event_presence_before_hours). */
export const PRESENCE_BEFORE_HOURS = 3;

function isFestivalLike(event) {
  return Boolean(event?.is_festival || event?.isFestival || event?.multi_day || event?.multiDay);
}

/**
 * The one rule for "may this phone use location for this event now" (Sam, 2026-10-12: never on a day with nothing
 * happening - not browsing, not on a map, no blue arrow): from PRESENCE_BEFORE_HOURS before the start to
 * getFindLiveUntil. False for an event with no date.
 */
export function isInPresenceWindow(event, now = Date.now()) {
  // getEventStartMs: a date-only event starts at midnight where it is, not UTC midnight - which opened the window at
  // 2 PM the afternoon before on the west coast (2026-10-16 audit).
  const start = getEventStartMs(event);
  const until = getFindLiveUntil(event);
  if (!Number.isFinite(start) || !until) return false;
  return now >= start - PRESENCE_BEFORE_HOURS * 3600 * 1000 && now < until.getTime();
}

/** When Find stops for this event: ends_at plus the tail; null when the event has no date. */
export function getFindLiveUntil(event) {
  const ends = getEventEndsAt(event);
  if (!ends) return null;
  const hours = isFestivalLike(event) ? FIND_TAIL_HOURS.festival : FIND_TAIL_HOURS.show;
  return new Date(ends.getTime() + hours * 60 * 60 * 1000);
}

export function isFindOver(event, now = new Date()) {
  const until = getFindLiveUntil(event);
  return until != null && now.getTime() >= until.getTime();
}

/** RSVP, going, and group joins close when the event is over. */
export function getEventActionCutoff(event) {
  return getEventEndsAt(event);
}

export function isEventActionOpen(event, now = new Date()) {
  return !isEventOver(event, now);
}

export function isEventActionClosed(event, now = new Date()) {
  return isEventOver(event, now);
}

/** The feed shows an event until it is over. */
export function getEventDisplayCutoff(event) {
  return getEventEndsAt(event);
}

export function isEventDisplayOpen(event, now = new Date()) {
  return !isEventOver(event, now);
}

export function isEventDisplayClosed(event, now = new Date()) {
  return isEventOver(event, now);
}

/**
 * When a group's chat is archived: 7 days after the event starts (archive_old_groups on the server). Not the
 * same as over - the group stays for photos and plans after; "This group closes tomorrow" keys on this.
 */
export function getGroupArchiveAt(event) {
  const raw = event?.event_datetime || event?.event_date || null;
  if (!raw) return null;
  const start = new Date(raw);
  if (Number.isNaN(start.getTime())) return null;
  return new Date(start.getTime() + 7 * DAY_MS);
}
